import React, { useState } from 'react';
import { Mail, Shield, RefreshCw, Lock } from 'lucide-react';
export default function EmailVerificationPage({ email, setPage, mode }) {
  const [isResetMode, setIsResetMode] = useState(mode === 'reset'); // ← accept "reset" mode from App.js

  const [otp, setOtp] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [otpVerifiedForReset, setOtpVerifiedForReset] = useState(false); // after verifying OTP for reset
  const API_BASE_URL = window._config?.apiBaseUrl || '';
  const DATABASE_KEY = window._config?.databaseKey || '';

  const apiFetch = (url, options = {}) =>
  fetch(`${API_BASE_URL}${url}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'X-Database-Key': DATABASE_KEY,
      ...(options.headers || {})
    }
  });
  
  const handleVerify = async () => {
    setLoading(true);
    setMessage('');
    setStatus('');
    try {
     const response = await fetch(
  `${API_BASE_URL}/VerifiedCustomer?databaseKey=${encodeURIComponent(DATABASE_KEY)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ Mail: email, Otp: otp })
      });

      const data = await response.json();
      const result = data?.VerifiedCustomerjs?.Table?.[0];

      if (result?.Status === 'Success') {
        if (isResetMode) {
          setOtpVerifiedForReset(true);
          setStatus('success');
          setMessage('OTP verified. Please enter new password.');
        } else {
          setStatus('success');
          setMessage(result.Message || 'Email verified successfully.');
          setTimeout(() => {
            setPage('login');
          }, 2000);
        }
      } else {
        setStatus('error');
        setMessage(result?.Message || 'Invalid OTP or Email.');
      }
    } catch (err) {
      console.error(err);
      setStatus('error');
      setMessage('An error occurred. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setLoading(true);
    setMessage('');
    setStatus('');
    try {
       const response = await fetch(
  `${API_BASE_URL}/ResendOtp?Mail=${encodeURIComponent(email)}&databaseKey=${encodeURIComponent(DATABASE_KEY)}`
);
      const data = await response.json();
      const result = data?.ResendOtpjs?.Table?.[0];

      if (result?.Status === 'Success') {
        setStatus('info');
        setMessage('OTP resent. Please check your email.');
      } else {
        setStatus('error');
        setMessage(result?.Message || 'Failed to resend OTP.');
      }
    } catch (err) {
      console.error(err);
      setStatus('error');
      setMessage('An error occurred while resending OTP.');
    } finally {
      setLoading(false);
    }
  };
const handleResetPassword = async () => {
  if (newPwd.trim().length < 5) {
    setStatus('error');
    setMessage('Password must be at least 5 characters long.');
    return;
  }

  setLoading(true);
  setMessage('');
  try {
   const response = await fetch(
  `${API_BASE_URL}/ResetPwd?Mail=${encodeURIComponent(email)}&Otp=${otp}&NewPwd=${encodeURIComponent(newPwd)}&databaseKey=${encodeURIComponent(DATABASE_KEY)}`
);
    const data = await response.json();
    const result = data?.ResetPwdjs?.Table?.[0];

    if (result?.Status === 'Success') {
      setStatus('success');
      setMessage(result.Message || 'Password reset successfully.');
      setTimeout(() => {
        setPage('login');
      }, 2000);
    } else {
      setStatus('error');
      setMessage(result?.Message || 'Failed to reset password.');
    }
  } catch (err) {
    console.error(err);
    setStatus('error');
    setMessage('Password reset failed. Try again.');
  } finally {
    setLoading(false);
  }
};

  const triggerForgotPasswordFlow = () => {
    setIsResetMode(true);
    setOtp('');
    setNewPwd('');
    setOtpVerifiedForReset(false);
    handleResendOtp(); // send OTP
  };

return (
  <div className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-pink-100 via-rose-50 to-teal-100 px-4 py-10 overflow-hidden">

    {/* Decorative floating elements */}
    <div className="absolute top-16 left-12 text-6xl opacity-20 animate-bounce">🌸</div>
    <div className="absolute bottom-20 right-16 text-5xl opacity-20 animate-pulse">🌷</div>

    <div className="backdrop-blur-xl bg-white/70 shadow-2xl rounded-3xl p-8 w-full max-w-md border border-white/40 transition-all duration-500">

      {/* Header */}
      <div className="text-center mb-6">
        <Mail className="text-teal-600 h-14 w-14 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-teal-700">
          {isResetMode ? "Reset Your Password" : "Verify Your Email"}
        </h2>
        <p className="text-gray-600 text-sm mt-2">
          A verification code has been sent to
        </p>
        <p className="font-semibold text-gray-800 text-sm">{email}</p>
      </div>

      {/* OTP Input */}
      {!otpVerifiedForReset && (
        <div className="relative mb-4">
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
            <Shield className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
            placeholder="Enter OTP"
            maxLength={6}
            className="w-full px-3 py-3 pl-12 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition"
          />
        </div>
      )}

      {/* Status Message */}
      {message && (
        <div
          className={`text-sm p-3 rounded-xl mb-4 ${
            status === "success"
              ? "text-green-700 bg-green-100"
              : status === "info"
              ? "text-blue-700 bg-blue-100"
              : "text-red-700 bg-red-100"
          }`}
        >
          {message}
        </div>
      )}

      {/* Main Button Section */}
      {!isResetMode || !otpVerifiedForReset ? (
        <button
          onClick={handleVerify}
          disabled={loading || otp.length === 0}
          className={`w-full py-3 rounded-xl font-semibold text-white shadow-lg transition transform ${
            loading
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-gradient-to-r from-teal-500 to-rose-400 hover:scale-105"
          }`}
        >
          {loading
            ? "Processing..."
            : isResetMode
            ? "Verify OTP"
            : "Verify Email"}
        </button>
      ) : (
        <>
          {/* New Password */}
          <div className="mt-4 relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <Lock className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="password"
              value={newPwd}
              onChange={(e) => setNewPwd(e.target.value)}
              placeholder="Enter New Password"
              className="w-full px-3 py-3 pl-12 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition"
            />
          </div>

          <button
            onClick={handleResetPassword}
            disabled={loading || !newPwd}
            className="mt-4 w-full py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-teal-500 to-rose-400 hover:scale-105 transform transition shadow-lg"
          >
            Reset Password
          </button>
        </>
      )}

      {/* Footer Links */}
      {!otpVerifiedForReset && (
        <div className="mt-6 flex flex-col sm:flex-row justify-between items-center gap-3 text-sm">

          <button
            onClick={handleResendOtp}
            className="flex items-center gap-2 text-teal-700 hover:text-rose-500 transition font-medium"
          >
            <RefreshCw className="h-4 w-4" />
            Resend OTP
          </button>

          <button
            onClick={triggerForgotPasswordFlow}
            className="flex items-center gap-2 text-gray-700 hover:text-rose-500 transition font-medium"
          >
            <Lock className="h-4 w-4" />
            Forgot Password?
          </button>

        </div>
      )}
    </div>
  </div>
);

}
